/**
 * Input Component
 * 
 * @description
 * Reusable input component with label, error state, and helper text.
 * Supports text, password, email, number, and search types.
 * 
 * @module components/ui/Input
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type InputVariant = 'default' | 'error';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label displayed above the input */
  label?: string;
  /** Error message displayed below the input */
  error?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Visual style variant */
  variant?: InputVariant;
  /** Full width input */
  fullWidth?: boolean;
  /** Left icon slot */
  leftIcon?: React.ReactNode;
  /** Right icon slot */
  rightIcon?: React.ReactNode;
}

/**
 * Input Component
 * 
 * @example
 * ```tsx
 * <Input 
 *   label="Email Address"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      variant = 'default',
      fullWidth = false,
      leftIcon,
      rightIcon,
      id,
      ...props
    },
    ref
  ) => {
    /**
     * Generate unique ID for accessibility
     */
    const inputId = id || `input-${Math.random().toString(36).slice(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    /**
     * Base input styles
     */
    const baseInputStyles = cn(
      'flex-1 h-10 px-3 py-2 rounded-lg',
      'bg-background text-foreground placeholder:text-foreground-subtle',
      'border transition-base',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background',
      'disabled:opacity-50 disabled:cursor-not-allowed'
    );

    /**
     * Variant-specific styles
     */
    const variants = {
      default: cn(
        'border-border',
        'focus:border-neon-cyan focus:ring-neon-cyan/50',
        'hover:border-border-hover'
      ),
      error: cn(
        'border-error',
        'focus:border-error focus:ring-error/50',
        'hover:border-error/80'
      ),
    };

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground-muted"
          >
            {label}
          </label>
        )}
        
        <div className="relative flex items-center">
          {leftIcon && (
            <span className="absolute left-3 text-foreground-subtle">
              {leftIcon}
            </span>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={cn(
              baseInputStyles,
              variants[variant],
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : helperText ? helperId : undefined}
            {...props}
          />
          
          {rightIcon && (
            <span className="absolute right-3 text-foreground-subtle">
              {rightIcon}
            </span>
          )}
        </div>
        
        {error && (
          <p id={errorId} className="text-sm text-error flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
        
        {helperText && !error && (
          <p id={helperId} className="text-sm text-foreground-subtle">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';